import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { askAITutor } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { 
  Sparkles, Send, Loader2, Lightbulb, Palette, 
  Camera, PenTool, RefreshCw, Trash2, Film, History, Brush
} from 'lucide-react';

export default function AITutor() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi ${user?.name?.split(' ')[0] || 'there'}! 👋 I'm your AI Art Tutor - your guide through the fascinating world of art, animation, and design!

I'm passionate about:
🎨 **Art History** - From Renaissance masters to Modern Art movements
🎬 **Animation History** - Disney classics to Pixar's revolution
🇯🇵 **Rise of Anime** - Studio Ghibli, Akira, and Japanese animation culture
🌈 **Color Theory** - The science and emotion behind every palette
✨ **VFX & 3D** - From practical effects to cutting-edge CGI

What creative journey shall we embark on today?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await askAITutor(userMessage);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        xp: response.data.xp_earned
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble responding right now. Please try again in a moment.",
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestion = (suggestion) => {
    setInput(suggestion);
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: `Chat cleared! How can I help you today?`
    }]);
  };

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  const suggestions = [
    { icon: History, text: 'Tell me about the Renaissance and its greatest artists' },
    { icon: Film, text: 'How did anime rise to global popularity?' },
    { icon: Palette, text: 'Explain color theory and how to create mood with colors' },
    { icon: Brush, text: 'What made Disney animation revolutionary?' },
    { icon: Camera, text: 'How has VFX evolved from practical to CGI?' },
    { icon: Lightbulb, text: 'What is the history of animation?' }
  ];

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="ai-tutor">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            AI Art Tutor
          </h1>
          <p className="text-muted-foreground">
            Your personal AI assistant for creative learning
          </p>
        </motion.div>

        {/* Chat Container */}
        <Card className={`h-[60vh] flex flex-col ${cardClass}`}>
          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <Avatar className={`w-8 h-8 flex-shrink-0 ${
                    message.role === 'user' ? 'bg-primary' : 'bg-gradient-to-br from-primary to-accent'
                  }`}>
                    <AvatarFallback className="text-white text-sm">
                      {message.role === 'user' ? user?.name?.charAt(0) : 'AI'}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block p-3 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}>
                      <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                    </div>
                    {message.xp && (
                      <p className="text-xs text-primary mt-1">+{message.xp} XP earned!</p>
                    )}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <Avatar className="w-8 h-8 bg-gradient-to-br from-primary to-accent">
                    <AvatarFallback className="text-white text-sm">AI</AvatarFallback>
                  </Avatar>
                  <div className="bg-muted rounded-2xl p-3">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Suggestions */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-muted-foreground mb-2">Suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestion(suggestion.text)}
                    className="text-xs"
                  >
                    <suggestion.icon className="w-3 h-3 mr-1" />
                    {suggestion.text}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-border">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={clearChat}
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything about art and design..."
                className="flex-1"
                disabled={loading}
                data-testid="ai-input"
              />
              <Button 
                type="submit" 
                disabled={loading || !input.trim()}
                className="btn-primary"
                data-testid="ai-send"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          </div>
        </Card>

        {/* Info Card */}
        <Card className={`mt-6 ${cardClass}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Earn XP by Learning!</p>
                <p className="text-sm text-muted-foreground">
                  Each question you ask earns you 5 XP towards your next level
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
